import {DashboardFeedbackDialog, type FeedbackDialogRef} from '@github-ui/dashboard-feedback-dialog'
import {Filter, type FilterProps} from '@github-ui/filter'
import {NewRepoAgentTaskButton} from '@github-ui/new-repo-agent-task-button/NewRepoAgentTaskButton'
import {noop} from '@github-ui/noop'
import {repoPullRequestsPath} from '@github-ui/paths'
import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {DashboardSidebar} from '@github-ui/shared-dashboard-sidebar/DashboardSidebar'
import {DashboardSidebarCollapsible} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsible'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {isStaff} from '@github-ui/stats'
import {reactFetch} from '@github-ui/verified-fetch'
import {AgentIcon, ArrowSwitchIcon, ArrowUpRightIcon, CommentIcon, MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, CounterLabel, Label} from '@primer/react'
import {useId, useRef, useState} from 'react'

import {useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardRepositoryToRepository} from '../helpers/repo-pulls-dashboard-proto'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import {REPO_PULLS_SIDEBAR_LINKS} from './repo-pulls-sidebar-links'
import {navigateToRepoPulls} from './navigate-to-repo-pulls'

import styles from './RepoPullsDashboardHeader.module.css'

const EMPTY_SAVED_VIEWS: never[] = []
const STAFF_FEEDBACK_URL = 'https://gh.io/pr-dashboard-feedback'
const REPO_PULLS_FEEDBACK_SUBJECT = 'repo_pulls_dashboard_feedback'

export function RepoPullsDashboardHeader() {
  const {fetchedQuery, fetchResults, generateLinkPath, owner, repo, selectedLink} = useRepoPullsSearch()
  const [query, setQuery] = useState(fetchedQuery)
  const [previousFetchedQuery, setPreviousFetchedQuery] = useState(fetchedQuery)
  const providers = useFilterProviders({repositoryScope: owner && repo ? `${owner}/${repo}` : undefined})
  const selectedHeaderLink = selectedLink?.id === 'all' ? {...selectedLink, name: 'All pull requests'} : selectedLink

  if (previousFetchedQuery !== fetchedQuery) {
    setPreviousFetchedQuery(fetchedQuery)
    setQuery(fetchedQuery)
  }

  const filterProps: FilterProps = {
    id: 'repo-pulls-dashboard-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: filterQuery => fetchResults({q: filterQuery.raw}),
    settings: {groupAndKeywordSupport: true},
  }

  return (
    <SharedHeader
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedHeaderLink}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
      previewMenu={<RepoPullsPreviewMenu />}
      showOptionsButton={false}
      showSavedViews={false}
      primaryAction={<RepoPullsDashboardActions />}
      filter={
        <div role="search" aria-label="Pull requests">
          <Filter {...filterProps} data-testid="repo-pulls-dashboard-filter" />
        </div>
      }
    />
  )
}

function RepoPullsPreviewMenu() {
  const {owner, repo} = useRepoPullsSearch()
  const reportError = useReportErrorContext()
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()
  if (!owner || !repo) return null

  return (
    <>
      <ActionMenu anchorRef={menuTriggerRef}>
        <ActionMenu.Button ref={menuTriggerRef} aria-label="Preview options" variant="invisible" size="small">
          <Label variant="success" className={styles.PreviewLabel}>
            Preview
          </Label>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList>
            {staff ? (
              <ActionList.LinkItem href={STAFF_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
                <ActionList.TrailingVisual>
                  <ArrowUpRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.LinkItem>
            ) : (
              <ActionList.Item onSelect={() => feedbackRef.current?.openDialog()}>
                <ActionList.LeadingVisual>
                  <CommentIcon />
                </ActionList.LeadingVisual>
                Give feedback
              </ActionList.Item>
            )}
            <ActionList.Divider />
            <ActionList.Item
              onSelect={async () => {
                try {
                  const response = await reactFetch('/feature_preview/repo_pulls_dashboard_surface/toggle', {
                    method: 'POST',
                  })
                  if (!response.ok) throw new Error('Failed to leave the repository pulls preview')
                  navigateToRepoPulls(repoPullRequestsPath(owner, repo))
                } catch (error) {
                  reportError(error)
                }
              }}
            >
              <ActionList.LeadingVisual>
                <ArrowSwitchIcon />
              </ActionList.LeadingVisual>
              Switch back to classic page
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {!staff && (
        <DashboardFeedbackDialog
          ref={feedbackRef}
          subject={REPO_PULLS_FEEDBACK_SUBJECT}
          placeholderText="Tell us your feedback on the new pull request experience"
          hideButton
          returnFocusRef={menuTriggerRef}
        />
      )}
    </>
  )
}

export function RepoPullsDashboardSidebar() {
  const {generateLinkPath, selectedLink} = useRepoPullsSearch()

  return (
    <DashboardSidebar
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedLink?.id || ''}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
    />
  )
}

export function RepoPullsDashboardCollapsibleSidebar() {
  const {generateLinkPath, selectedLink} = useRepoPullsSearch()

  return (
    <DashboardSidebarCollapsible
      dashboardType="pull-requests"
      links={REPO_PULLS_SIDEBAR_LINKS}
      selectedLink={selectedLink?.id || ''}
      onSelectLink={noop}
      savedViews={EMPTY_SAVED_VIEWS}
      generateLinkPath={generateLinkPath}
    />
  )
}

function RepoPullsDashboardActions() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const actions = layoutPayload?.actions
  const disabledReasonId = useId()
  if (!actions) return null

  const {agentTaskRepository, labels, milestones, newPullRequest} = actions
  return (
    <div className="d-flex flex-wrap gap-2">
      {labels ? (
        <Button as={Link} to={labels.url} leadingVisual={TagIcon}>
          Labels <CounterLabel>{labels.count}</CounterLabel>
        </Button>
      ) : null}
      {milestones ? (
        <Button as={Link} to={milestones.url} leadingVisual={MilestoneIcon}>
          Milestones <CounterLabel>{milestones.count}</CounterLabel>
        </Button>
      ) : null}
      {agentTaskRepository ? (
        <NewRepoAgentTaskButton
          repository={repoPullsDashboardRepositoryToRepository(agentTaskRepository)}
          icon={AgentIcon}
          label="New agent session"
          eventType="new_agent_task_button_repo_pulls"
        />
      ) : null}
      {newPullRequest?.url ? (
        <Button as={Link} to={newPullRequest.url} variant="primary">
          {newPullRequest.label}
        </Button>
      ) : newPullRequest?.disabledReason ? (
        <>
          <Button
            variant="primary"
            inactive
            aria-disabled="true"
            aria-describedby={disabledReasonId}
            title={newPullRequest.disabledReason}
          >
            {newPullRequest.label}
          </Button>
          <span id={disabledReasonId} className="sr-only">
            {newPullRequest.disabledReason}
          </span>
        </>
      ) : null}
    </div>
  )
}
